import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-slate-950 dark relative overflow-hidden grain">
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <Link to="/" className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
              <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-black tracking-tight">QUOTEBOT</span>
          </Link>
          <div className="text-white">
            <p className="font-display text-4xl font-black leading-tight tracking-tight mb-4">
              "Quotes in 90 seconds.<br /> Book more jobs before lunch."
            </p>
            <p className="text-sm text-slate-400">— Kevin R., Pressure Washing Pro, Tampa FL</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
                <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
              </div>
              <span className="font-display text-2xl font-black tracking-tight">QUOTEBOT</span>
            </Link>
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight mb-2">Log in</h1>
          <p className="text-muted-foreground mb-8">Get back to closing quotes.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-sm mt-1" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-sm mt-1" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-12 rounded-sm btn-kinetic">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            No account? <Link to="/register" className="text-primary font-medium hover:underline">Start free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
